import { useQuery, useQueryClient } from '@tanstack/react-query';
import { collection, getDocs, query, orderBy, Timestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../lib/firebase';
import type { Booking } from '@shared/types';
import toast from 'react-hot-toast';

export default function Bookings() {
  const queryClient = useQueryClient();

  const { data: bookings, isLoading } = useQuery({
    queryKey: ['admin-bookings'],
    queryFn: async () => {
      const q = query(collection(db, 'bookings'), orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      return snapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          checkinTimestamp: data.checkinTimestamp instanceof Timestamp ? data.checkinTimestamp.toDate() : data.checkinTimestamp,
          checkoutTimestamp: data.checkoutTimestamp instanceof Timestamp ? data.checkoutTimestamp.toDate() : data.checkoutTimestamp,
          createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : data.createdAt,
          updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : data.updatedAt,
        };
      }) as Booking[];
    },
  });

  const sendPayFastLink = async (bookingId: string) => {
    try {
      const fn = httpsCallable(functions, 'createPayFastPaymentLink');
      const result = await fn({ bookingId });
      const data = result.data as { token?: string; message?: string };
      if (data.token) {
        const url = `${window.location.origin}/payfast-checkout?token=${data.token}`;
        toast.success(data.message || 'Payment link ready');
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        toast.success(data.message || 'Done');
      }
      queryClient.invalidateQueries({ queryKey: ['admin-bookings'] });
    } catch (e: any) {
      toast.error(e.message || 'Failed to create PayFast link. Check Functions config.');
    }
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="flex justify-center items-center min-h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#32373c]"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="text-3xl font-bold mb-2">Bookings</h1>
      <p className="text-gray-600 text-sm mb-6">
        Pending = request received. Use &quot;PayFast&quot; to generate a payment link (emailed to guest if SMTP + APP_PUBLIC_URL are set) and open checkout in a new tab.
      </p>
      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">ID</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Dates</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {bookings?.map((booking) => {
              const checkIn = booking.checkinTimestamp.toLocaleDateString('en-ZA');
              const checkOut = booking.checkoutTimestamp.toLocaleDateString('en-ZA');
              const isPending = booking.status === 'pending';
              const unpaid = booking.payment?.captureStatus !== 'captured';
              return (
                <tr key={booking.id}>
                  <td className="px-4 py-4 whitespace-nowrap text-sm font-mono">{booking.id.slice(0, 8)}…</td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm">
                    {checkIn} – {checkOut}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm">
                    R {(booking.totalCents / 100).toFixed(2)}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        booking.status === 'confirmed'
                          ? 'bg-green-100 text-green-800'
                          : booking.status === 'cancelled'
                          ? 'bg-red-100 text-red-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}
                    >
                      {booking.status}
                    </span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm">
                    {isPending && unpaid && (
                      <button
                        type="button"
                        onClick={() => sendPayFastLink(booking.id)}
                        className="btn btn-primary text-xs py-1 px-3"
                      >
                        PayFast link
                      </button>
                    )}
                    {booking.status === 'confirmed' && booking.payment?.captureStatus === 'captured' && (
                      <span className="text-green-600 text-xs">Paid</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
